// Implements: F-PRV-006, F-PRV-011, F-PRV-016
/**
 * The widths the viewer can show, as plain numbers.
 *
 * A width is the frame's LAYOUT viewport in CSS pixels: the document inside evaluates its media
 * queries and viewport units against it. Zoom is a separate number that only changes how large the
 * frame looks on screen, so the two never mix.
 */
export type ViewportMode = "desktop" | "tablet" | "mobile" | "free";

export interface ViewportState {
  readonly mode: ViewportMode;
  readonly freeWidth: number;
  readonly zoom: number;
}

export const VIEWPORT_PRESETS = { desktop: 1280, tablet: 768, mobile: 375 } as const;
export const FREE_MIN_WIDTH = 320;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;

export const DEFAULT_VIEWPORT: ViewportState = { mode: "desktop", freeWidth: 1024, zoom: 1 };

const MODES: readonly ViewportMode[] = ["desktop", "tablet", "mobile", "free"];

/**
 * The layout width of the frame. A preset is exact (a narrow window scrolls instead of changing the
 * measured width); a free width is raised to the floor and cut to the available space, but the
 * floor wins when the space is smaller than it.
 */
export function frameWidth(state: ViewportState, available: number | null): number {
  if (state.mode !== "free") return VIEWPORT_PRESETS[state.mode];
  const ceiling = available === null ? Number.POSITIVE_INFINITY : Math.max(FREE_MIN_WIDTH, available);
  return Math.min(Math.max(Math.round(state.freeWidth), FREE_MIN_WIDTH), ceiling);
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return Math.min(Math.max(zoom, ZOOM_MIN), ZOOM_MAX);
}

export function stepZoom(zoom: number, direction: 1 | -1): number {
  return clampZoom(zoom + direction * ZOOM_STEP);
}

function isMode(value: unknown): value is ViewportMode {
  return typeof value === "string" && (MODES as readonly string[]).includes(value);
}

/** Whatever was stored, as a valid state: a corrupt or foreign value gives the default. */
export function parseViewport(raw: string | null): ViewportState {
  if (raw === null) return DEFAULT_VIEWPORT;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return DEFAULT_VIEWPORT;
  }
  if (typeof value !== "object" || value === null) return DEFAULT_VIEWPORT;
  const { mode, freeWidth, zoom } = value as Record<string, unknown>;
  if (!isMode(mode)) return DEFAULT_VIEWPORT;
  const width = typeof freeWidth === "number" ? freeWidth : NaN;
  const level = typeof zoom === "number" ? zoom : NaN;
  return {
    mode,
    freeWidth: Number.isFinite(width)
      ? Math.max(Math.round(width), FREE_MIN_WIDTH)
      : DEFAULT_VIEWPORT.freeWidth,
    zoom: clampZoom(level),
  };
}
